import React, { useCallback, useEffect, useState } from 'react';
import { guidanceAccessibilityManager } from '../../utils/accessibilityUtils';
import { logger } from '../../utils/logger';
import '../../styles/DashboardGuide.css';

import { DEFAULT_GUIDE_STEPS } from './defaultSteps';
import { GuideTooltip } from './GuideTooltip';
import { getAnimationClass } from './helpers';
import { useGuidePositioning } from './useGuidePositioning';
import type { DashboardGuideProps } from './types';

export type {
  ActionButton,
  AnimationType,
  DashboardGuideProps,
  GuideStep,
  TooltipPosition,
} from './types';

export const DashboardGuide: React.FC<DashboardGuideProps> = ({
  isActive,
  currentStep,
  onStepChange,
  onComplete,
  onDismiss,
  steps = DEFAULT_GUIDE_STEPS,
  showProgress = true,
  allowSkip = true,
  autoAdvance = false,
  animationsEnabled = true,
  onError,
}) => {
  const currentGuideStep = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const totalSteps = steps.length;

  const { tooltipRef, targetElement, tooltipPosition, highlightPosition, isPositioning } =
    useGuidePositioning({
      isActive,
      currentStep,
      currentGuideStep,
      isLastStep,
      onStepChange,
      onComplete,
      onError,
    });

  // Track step start time for analytics/telemetry hooks
  const [, setStepStartTime] = useState<number>(Date.now());
  useEffect(() => {
    setStepStartTime(Date.now());
  }, [currentStep]);

  const handleNext = useCallback(() => {
    // Call step exit callback
    if (currentGuideStep?.onStepExit) {
      currentGuideStep.onStepExit();
    }

    if (isLastStep) {
      // Announce completion
      guidanceAccessibilityManager.announceCompletion();
      onComplete();
    } else {
      // Announce step change
      const nextStep = steps[currentStep + 1];
      if (nextStep) {
        guidanceAccessibilityManager.announceStepChange(
          nextStep.title,
          currentStep + 2,
          totalSteps,
        );
      }
      onStepChange(currentStep + 1);
    }
  }, [currentStep, currentGuideStep, isLastStep, onComplete, onStepChange, steps, totalSteps]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      // Call step exit callback
      if (currentGuideStep?.onStepExit) {
        currentGuideStep.onStepExit();
      }
      onStepChange(currentStep - 1);
    }
  }, [currentStep, currentGuideStep, onStepChange]);

  const handleSkip = useCallback(() => {
    // Announce dismissal
    guidanceAccessibilityManager.announceDismissal();

    // Call step exit callback
    if (currentGuideStep?.onStepExit) {
      currentGuideStep.onStepExit();
    }

    onDismiss();
  }, [currentGuideStep, onDismiss]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          handleSkip();
          break;
        case 'ArrowRight':
        case ' ':
        case 'Enter':
          event.preventDefault();
          handleNext();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (currentStep > 0) {
            handlePrevious();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, currentStep, isLastStep, handleNext, handlePrevious, handleSkip]);

  const handleActionButton = useCallback(() => {
    if (currentGuideStep?.actionButton) {
      try {
        currentGuideStep.actionButton.action();

        // Auto-advance if configured
        if (autoAdvance) {
          setTimeout(handleNext, 1500);
        }
      } catch (error) {
        logger.error('[Guide] Action button error', { error });
        if (onError) {
          onError(
            error instanceof Error ? error : new Error('Action button error'),
            currentGuideStep.id,
          );
        }
      }
    }
  }, [currentGuideStep, autoAdvance, handleNext, onError]);

  if (!isActive || !currentGuideStep) {
    return null;
  }

  // Show loading state if positioning or target not found
  if (!targetElement || isPositioning) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center"
        style={{ zIndex: 9999 }}
        role="presentation"
        aria-hidden="true"
      >
        <div className="guide-tooltip rounded-lg p-6 max-w-sm bg-white dark:bg-gray-800 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-600 dark:text-gray-300">Loading guide...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop — invisible, captures clicks outside spotlight to dismiss */}
      <div
        data-testid="guide-backdrop"
        className="fixed inset-0"
        style={{ zIndex: 9997 }}
        aria-hidden="true"
        onClick={allowSkip ? handleSkip : undefined}
      />

      {/* Spotlight — box-shadow in CSS creates the dark backdrop; this div is the cutout */}
      <div
        className={`guide-highlight ${getAnimationClass(currentGuideStep.animation, animationsEnabled)}`}
        style={{
          position: 'fixed',
          top: highlightPosition.top,
          left: highlightPosition.left,
          width: highlightPosition.width,
          height: highlightPosition.height,
          borderRadius: '12px',
          pointerEvents: 'none',
          zIndex: 9998,
        }}
        aria-hidden="true"
      />

      <GuideTooltip
        tooltipRef={tooltipRef}
        currentGuideStep={currentGuideStep}
        tooltipPosition={tooltipPosition}
        isPositioning={isPositioning}
        allowSkip={allowSkip}
        showProgress={showProgress}
        steps={steps}
        currentStep={currentStep}
        totalSteps={totalSteps}
        isLastStep={isLastStep}
        onSkip={handleSkip}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onActionButton={handleActionButton}
      />
    </>
  );
};

export default DashboardGuide;
